import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { accountStatusView } from "@/lib/account";

/* Building blocks from design/concept-a-guided.html: card, pill, small button. */

export type Tone = "ok" | "warn" | "bad" | "info" | "mute";

const TONE: Record<Tone, string> = {
  ok: "bg-[var(--success-soft)] text-[var(--success)]",
  warn: "bg-[var(--warning-soft)] text-[var(--warning)]",
  bad: "bg-[var(--danger-soft)] text-[var(--danger)]",
  info: "bg-[var(--info-soft)] text-[var(--info)]",
  mute: "bg-[var(--bg-hover)] text-[var(--text-muted)]",
};

export function Pill({ tone, dot = true, children }: { tone: Tone; dot?: boolean; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-[5px] whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium",
        TONE[tone]
      )}
    >
      {dot && <i className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  );
}

/** Running / Starting / Error / Stopped chip for a live strategy. */
export function statusPill(status?: string, enabled = true) {
  if (!enabled || status === "stopped") return <Pill tone="mute">Stopped</Pill>;
  if (status === "error") return <Pill tone="bad">Error</Pill>;
  if (status === "running") return <Pill tone="ok">Running</Pill>;
  return <Pill tone="info">Starting</Pill>;
}

export function SmallButton({
  to,
  variant = "default",
  className,
  children,
}: {
  to: string;
  variant?: "default" | "primary" | "ghost";
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={cn(
        "btn btn-sm",
        variant === "primary" ? "btn-primary" : variant === "ghost" ? "btn-ghost" : "btn-soft",
        className
      )}
    >
      {children}
    </Link>
  );
}

export function Card({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <section className={cn("min-w-0 overflow-hidden rounded-[14px] border border-[var(--border)] bg-[var(--bg-card)]", className)}>
      {children}
    </section>
  );
}

export function CardHead({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 px-4 pb-2 pt-4">
      <h2 className="m-0 text-[15px] font-semibold text-[var(--text-primary)]">{title}</h2>
      {children}
    </div>
  );
}

export { ExchangeMark } from "@/components/ExchangeMark";

export function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="whitespace-nowrap rounded-[6px] border border-[var(--border)] px-1.5 py-px text-[11px] text-[var(--text-muted)]">
      {children}
    </span>
  );
}

export const eyebrow = "text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--text-muted)]";

/** Connected / Connecting / Error / Off pill for an exchange account. */
export function accountPill(account: { status?: string; enabled?: boolean }) {
  const view = accountStatusView(account.status, account.enabled);
  if (view.tone === "running") return <Pill tone="ok">Connected</Pill>;
  if (view.tone === "connecting") return <Pill tone="info">Connecting</Pill>;
  if (view.tone === "error") return <Pill tone="bad">Error</Pill>;
  return <Pill tone="mute">{account.enabled ? "Stopped" : "Off"}</Pill>;
}

/** Form field label (design `.field label`). */
export const fieldLabel = "text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]";
